import { Pause, Play } from 'lucide-react';
import { useMemo, useState } from 'react';

import { ExploreChallenges } from './ExploreChallenges';
import { WorkedStepsPanel } from './ModelInputs';
import { formatModelNumber } from './modelInputContract';
import { HeatingSetupPanel, HeatingWorkingPanel } from './ParticleHeatingControls';
import { HeatingCurveChart } from './ParticleModelScenes';
import { WATER_PHASE_NAMES, type HeatingPhase } from './particleModel';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';
import { WaterBeaker3D } from './WaterBeaker3D';
import {
  COMPARISON_START_C,
  PROPERTIES_CHALLENGES,
  PROPERTIES_QUESTION,
  WATER_EXPLORE_HEATING_DEFAULT,
  specificHeatComparison,
} from './waterChemistryExplore';
import { BEAKER_CAPACITY_G, HEATING_MODEL_MAX_C, calculateHeating, timeToReach } from './waterHeatingCalc';

const PERCENT = 100;

/** Shortcuts that set the heating time for the learner's own mass, start and power. */
const PRESETS: Array<{ id: string; label: string; phase: HeatingPhase; fraction: number }> = [
  { id: 'warm', label: 'Water half warmed', phase: 'liquid', fraction: 0.5 },
  { id: 'boil-start', label: 'Starts to boil', phase: 'boiling', fraction: 0 },
  { id: 'half-boiled', label: 'Half boiled away', phase: 'boiling', fraction: 0.5 },
];

/**
 * Explore view of water's high specific heat capacity: the shared heating explorer (inputs, worked
 * stages, 3D beaker, heating curve) plus the same energy into the same mass of other substances.
 * The learning question and tasks sit directly above the inputs (CT-INTERACT C7).
 */
export function WaterChemistryPropertiesExplore() {
  const [setup, setSetup] = useState(WATER_EXPLORE_HEATING_DEFAULT);
  const reducedMotion = usePrefersReducedMotion();
  const [playing, setPlaying] = useState<boolean | null>(null);
  const moving = playing ?? !reducedMotion;
  const calculation = useMemo(() => calculateHeating(setup), [setup]);
  const comparison = useMemo(() => specificHeatComparison(calculation.energyJ, setup.massG), [calculation.energyJ, setup.massG]);
  const challengeInput = useMemo(() => ({ setup, comparison }), [setup, comparison]);
  const { state } = calculation;
  const maxRise = Math.max(...comparison.rows.map((row) => row.deltaTK ?? 0), 1);
  const waterRise = comparison.rows.find((row) => row.id === 'water')?.deltaTK ?? null;

  return (
    <section
      aria-label="Explore water heating"
      className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <p className="max-w-2xl text-sm font-medium leading-6 text-slate2">
          Choose the mass, starting temperature, heater power and time. Pure water at 101.325 kPa; the model stops at{' '}
          {HEATING_MODEL_MAX_C} °C and ignores heat lost to the room. Then compare how far the same energy warms the same mass
          of other substances.
        </p>
        <button
          type="button"
          onClick={() => setPlaying(!moving)}
          className="inline-flex items-center gap-2 rounded-full border border-hairline px-4 py-2 text-sm font-extrabold text-ink"
        >
          {moving ? <Pause className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
          {moving ? 'Pause motion' : 'Play motion'}
        </button>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <div className="min-w-0">
          <div>
            <HeatingSetupPanel setup={setup} onChange={setSetup} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Heating presets">
            {PRESETS.map((preset) => {
              const timeS = timeToReach(setup, preset.phase, preset.fraction);
              return (
                <button
                  key={preset.id}
                  type="button"
                  disabled={timeS === null}
                  onClick={() => timeS !== null && setSetup({ ...setup, timeS })}
                  className="rounded-full border border-hairline bg-surface px-3 py-1.5 text-xs font-extrabold text-ink hover:border-brand-sky/50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
          <div className="mt-4">
            <ExploreChallenges question={PROPERTIES_QUESTION} challenges={PROPERTIES_CHALLENGES} result={challengeInput} />
          </div>
        </div>
        <div className="min-w-0">
          <p className="text-2xl font-extrabold text-ink" data-testid="water-explore-state">
            {WATER_PHASE_NAMES[state.phase]}
            <span className="ml-2 text-xl font-extrabold text-brand-coral">{formatModelNumber(state.temperatureC)} °C</span>
          </p>
          <figure className="mt-3 min-w-0">
            <figcaption className="mb-2 text-xs font-extrabold uppercase tracking-[0.12em] text-slate2">What you see</figcaption>
            <WaterBeaker3D
              state={{ phase: state.phase, fractions: state.fractions, temperatureC: state.temperatureC, fill: setup.massG / BEAKER_CAPACITY_G }}
              playing={moving}
              label={`Beaker of ${formatModelNumber(setup.massG, 0)} g of water at ${formatModelNumber(state.temperatureC)} °C: ${WATER_PHASE_NAMES[state.phase]}`}
            />
          </figure>
        </div>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <figure className="min-w-0 rounded-3xl border border-hairline bg-surface p-4" aria-label="Temperature rise for the same energy">
          <ul className="grid gap-2">
            {comparison.rows.map((row) => (
              <li
                key={row.id}
                className="grid grid-cols-[6.5rem_minmax(0,1fr)_4.5rem] items-center gap-2 text-xs font-bold text-ink-soft"
                aria-label={`${row.name}: ${row.deltaTK === null ? 'beyond the comparison limit' : `${formatModelNumber(row.deltaTK)} °C rise`}`}
              >
                <span className={`truncate ${row.id === 'water' ? 'font-extrabold text-ink' : ''}`}>{row.name}</span>
                <span className="relative h-5 rounded-full bg-canvas-pure" aria-hidden="true">
                  <span
                    className={`absolute inset-y-0.5 left-0 rounded-full ${
                      row.deltaTK === null ? 'border border-dashed border-brand-coral bg-wash-coral' : row.id === 'water' ? 'bg-brand-coral' : 'bg-brand-sky'
                    }`}
                    style={{ width: `${row.deltaTK === null ? PERCENT : Math.max((row.deltaTK / maxRise) * PERCENT, 1)}%` }}
                  />
                </span>
                <span className="text-right">{row.deltaTK === null ? 'limit' : `+${formatModelNumber(row.deltaTK)} °C`}</span>
              </li>
            ))}
          </ul>
          <figcaption className="mt-3 text-xs font-medium leading-5 text-slate2">
            Temperature rise for the same energy and mass, from {COMPARISON_START_C} °C. A dashed bar means the substance reaches
            its limit first.
          </figcaption>
        </figure>
        <div className="min-w-0">
          <HeatingCurveChart segments={calculation.segments} energyJ={state.energyJ} temperatureC={state.temperatureC} massG={setup.massG} />
        </div>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <div className="min-w-0">
          <HeatingWorkingPanel result={calculation} massG={setup.massG} />
        </div>
        <WorkedStepsPanel
          title="Same energy, same mass: other substances"
          lead={`${calculation.energyWorking} into ${formatModelNumber(setup.massG)} g of each substance, starting at ${COMPARISON_START_C} °C`}
          leadTestId="comparison-energy"
          steps={comparison.rows.map((row) => ({
            id: row.id,
            title: row.name,
            formula: row.formula,
            working: row.working,
            status: row.deltaTK === null ? 'current' : 'done',
            note: `c = ${row.specificHeat} J/(g·°C)`,
          }))}
          stepTestIdPrefix="comparison"
          result={
            waterRise === null
              ? 'Result: every substance reaches its limit. Add less energy to compare the rises.'
              : `Result: water warms least (+${formatModelNumber(waterRise)} °C) because it has the highest specific heat capacity.`
          }
          resultTestId="comparison-result"
          limitNote={
            comparison.beyondCount > 0
              ? `${comparison.beyondCount} of ${comparison.rows.length} substances reach their limit (boiling point for a liquid, 100 °C for a solid) before all the energy is used. The reviewed c values are near 25 °C, so the model stops there instead of guessing.`
              : null
          }
        />
      </div>
    </section>
  );
}
